import React, { useState } from 'react';
import OrbitingTagCloud from '../components/OrbitingTagCloud.jsx';
import PageCTA from '../components/sections/pageCTA.jsx';

// ─── ICON ──────────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
    strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);

const ICONS = {
  arrow: "M5 12h14M12 5l7 7-7 7",
  heka: "M13 10V3L4 14h7v7l9-11h-7z",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8M12 12m-3 0a3 3 0 106 0 3 3 0 10-6 0",
  target: "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm0-6a4 4 0 100-8 4 4 0 000 8zm0-2a2 2 0 100-4 2 2 0 000 4z",
  compass: "M12 2a10 10 0 100 20A10 10 0 0012 2zm0 0v2m0 16v2M2 12h2m16 0h2M16.24 7.76l-1.42 1.42M9.17 14.83l-1.41 1.41M16.24 16.24l-1.41-1.41M9.18 9.17L7.76 7.76",
  spark: "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  brain: "M9.5 2A2.5 2.5 0 0112 4.5v15a2.5 2.5 0 01-4.96-.44 2.5 2.5 0 01-2.96-3.08 3 3 0 01-.34-5.58 2.5 2.5 0 013.32-3.97A2.5 2.5 0 019.5 2zM14.5 2A2.5 2.5 0 0112 4.5v15a2.5 2.5 0 004.96-.44 2.5 2.5 0 002.96-3.08 3 3 0 00.34-5.58 2.5 2.5 0 00-3.32-3.97A2.5 2.5 0 0014.5 2z",
  workflow: "M22 12h-4l-3 9L9 3l-3 9H2",
  layers: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  rocket: "M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09zM12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z",
  connect: "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75",
  foundry: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8",
  noesis: "M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z",
  whatsapp: "M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z",
};

// ─── STYLES ────────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

   @keyframes vp-neural-pulse {
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50%      { opacity: 1;   transform: scale(1.3); }
  }
  .vp-neural-node {
    animation: vp-neural-pulse 2s ease-in-out infinite;
  }

  @keyframes vp-connection-fade {
    0%, 100% { opacity: 0.2; }
    50%      { opacity: 0.5; }
  }
  .vp-neural-connections line {
    animation: vp-connection-fade 2s ease-in-out infinite;
  }

  @keyframes vp-scan {
    0%   { stroke-dashoffset: 100; }
    100% { stroke-dashoffset: 0; }
  }
  .vp-scan-line {
    stroke-dasharray: 100;
    animation: vp-scan 2s ease-in-out infinite;
  }

  .vp-principle-card {
    transition: all 0.3s cubic-bezier(0.22,1,0.36,1);
  }
  .vp-principle-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.3);
  }

  /* ── Shimmer title ── */
  .hp-title-gradient {
    background: linear-gradient(110deg, #fbbf24 30%, #a78bfa 50%, #fbbf24 70%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: hp-shimmer 8s linear infinite;
  }
  @keyframes hp-shimmer {
    0%   { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }

  /* ── Fade up ── */
  @keyframes hp-fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .hp-fade   { animation: hp-fadeUp 0.8s ease forwards; }
  .hp-fade-1 { animation-delay: 0.05s; opacity: 0; }
  .hp-fade-2 { animation-delay: 0.15s; opacity: 0; }
  .hp-fade-3 { animation-delay: 0.25s; opacity: 0; }
  .hp-fade-4 { animation-delay: 0.35s; opacity: 0; }
  .hp-fade-5 { animation-delay: 0.45s; opacity: 0; }
  .hp-fade-6 { animation-delay: 0.55s; opacity: 0; }

  /* ── Divider ── */
  .hp-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(251,191,36,0.3), transparent);
  }

  /* ── Dot grid ── */
  .hp-dot-grid {
    background-image: radial-gradient(circle, rgba(251,191,36,0.1) 1px, transparent 1px);
    background-size: 28px 28px;
  }

  /* ── Card hover ── */
  .hp-card {
    transition: all 0.3s cubic-bezier(0.22,1,0.36,1);
  }
  .hp-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.35);
  }

  /* ── Manifesto ticker ── */
  .hp-manifesto-item {
    transition: all 0.25s ease;
    cursor: default;
  }
  .hp-manifesto-item:hover {
    color: rgba(251,191,36,0.9);
    padding-left: 8px;
  }

  /* ── Node pulse ── */
  @keyframes hp-node-pulse {
    0%, 100% { opacity: 0.6; transform: scale(1); }
    50%       { opacity: 1;   transform: scale(1.2); }
  }
  .hp-node { animation: hp-node-pulse 2s ease-in-out infinite; }

  /* ── Flow line ── */
  @keyframes hp-flow {
    0%   { stroke-dashoffset: 20; }
    100% { stroke-dashoffset: 0; }
  }
  .hp-flow-line {
    stroke-dasharray: 5 3;
    animation: hp-flow 1.8s linear infinite;
  }

  /* ── Platform card glow on hover ── */
  .hp-platform-foundry:hover  { border-color: rgba(251,191,36,0.5)  !important; box-shadow: 0 0 40px rgba(251,191,36,0.08);  }
  .hp-platform-noesis:hover   { border-color: rgba(139,92,246,0.5)   !important; box-shadow: 0 0 40px rgba(139,92,246,0.08);  }

  /* ── Product tag ── */
  .hp-product-tag {
    transition: all 0.3s ease;
  }
  .hp-product-tag:hover {
    transform: translateY(-2px);
  }

  /* ── Principle card ── */
  .hp-principle-card {
    transition: all 0.3s cubic-bezier(0.22,1,0.36,1);
  }
  .hp-principle-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.3);
  }

  /* ── Transformation cycle arrows ── */
  @keyframes hp-arrow-flow {
    0%   { stroke-dashoffset: 40; opacity: 0.4; }
    100% { stroke-dashoffset: 0;  opacity: 1;   }
  }
  .hp-arrow { 
    stroke-dasharray: 8 4;
    animation: hp-arrow-flow 1.4s linear infinite;
  }

  /* ── CTA mesh ── */
  .hp-cta-mesh {
    background-image:
      radial-gradient(at 20% 50%, rgba(251,191,36,0.08) 0px, transparent 50%),
      radial-gradient(at 80% 50%, rgba(139,92,246,0.08) 0px, transparent 50%),
      radial-gradient(at 50% 90%, rgba(6,182,212,0.05) 0px, transparent 50%);
  }
  .hp-grid-lines {
    background-image:
      linear-gradient(rgba(251,191,36,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(251,191,36,0.025) 1px, transparent 1px);
    background-size: 40px 40px;
  }

  /* ── hekaos-line reveal ── */
  .hekaos-line {
    display: block;
    opacity: 0;
    animation: hp-fadeUp 0.7s ease forwards;
  }
  .hekaos-line:nth-child(1) { animation-delay: 0.3s; }
  .hekaos-line:nth-child(2) { animation-delay: 0.5s; }
  .hekaos-line:nth-child(3) { animation-delay: 0.7s; }
  .hekaos-line:nth-child(4) { animation-delay: 0.9s; }

  .hekaos-title {
    background: linear-gradient(110deg, #fbbf24, #a78bfa, #fbbf24);
    background-size: 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: hp-shimmer 6s linear infinite;
  }
`;



// ─── TRANSFORMATION CYCLE ──────────────────────────────────────────────────────
const TransformationCycle = () => (
<section className="hp-fade hp-fade-3 py-16">
      <div className="text-center mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">The Core Loop</p>
      <h2 className="text-2xl font-semibold text-slate-100 tracking-tight">
        From Raw Signal to Engineered Intelligence
      </h2>
    </div>

    {/* Desktop */}
    <div className="hidden md:flex items-center justify-center gap-0 max-w-4xl mx-auto">
      {/* RAW */}
      <div className="flex flex-col items-center gap-4 w-48">
        <div className="w-20 h-20 rounded-2xl border border-red-800/40 bg-red-900/20 flex items-center justify-center"
          style={{ boxShadow: "0 0 28px rgba(239,68,68,0.1)" }}>
          <Icon d={ICONS.spark} size={32} className="text-red-400" />
        </div>
        <div className="text-center">
          <p className="text-red-400 font-bold text-sm tracking-widest uppercase mb-1">RAW</p>
          <p className="text-slate-500 text-xs leading-relaxed">Unformed signal.<br />Potential without structure.</p>
        </div>
      </div>

      {/* Arrow 1 */}
      <div className="flex flex-col items-center gap-2 w-24 flex-shrink-0 pb-12">
        <svg width="96" height="20" viewBox="0 0 96 20" fill="none">
          <line x1="0" y1="10" x2="80" y2="10" stroke="#ef4444" strokeWidth="1.5" className="hp-arrow" />
          <polyline points="70,4 84,10 70,16" stroke="#ef4444" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-slate-600 text-xs font-semibold tracking-widest uppercase">FOUNDRY</span>
      </div>

      {/* HEKA — center */}
      <div className="flex flex-col items-center gap-4 relative">
        <div className="absolute w-32 h-32 rounded-full border border-violet-500/15"
          style={{ animation: "hp-node-pulse 3s ease-in-out infinite" }} />
        <div className="w-32 h-32 rounded-2xl border border-violet-600/50 flex items-center justify-center relative z-10"
          style={{
            background: "linear-gradient(135deg, rgba(109,40,217,0.28), rgba(139,92,246,0.16))",
            boxShadow: "0 0 48px rgba(139,92,246,0.22), inset 0 1px 0 rgba(167,139,250,0.15)",
          }}>
          <Icon d={ICONS.heka} size={46} className="text-violet-400" />
        </div>
        <div className="text-center mt-1">
          <p className="text-violet-400 font-bold text-base tracking-widest uppercase mb-1">HEKA</p>
          <p className="text-slate-500 text-xs leading-relaxed max-w-[10rem]">Intelligence engine.<br />Pattern from noise.</p>
        </div>
      </div>

      {/* Arrow 2 */}
      <div className="flex flex-col items-center gap-2 w-24 flex-shrink-0 pb-12">
        <svg width="96" height="20" viewBox="0 0 96 20" fill="none">
          <line x1="0" y1="10" x2="80" y2="10" stroke="#22d3ee" strokeWidth="1.5" className="hp-arrow" style={{ animationDelay: "0.6s" }} />
          <polyline points="70,4 84,10 70,16" stroke="#22d3ee" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-slate-600 text-xs font-semibold tracking-widest uppercase">NOESIS</span>
      </div>

      {/* ENGINEERED */}
      <div className="flex flex-col items-center gap-4 w-48">
        <div className="w-20 h-20 rounded-2xl border border-cyan-800/40 bg-cyan-900/20 flex items-center justify-center"
          style={{ boxShadow: "0 0 28px rgba(34,211,238,0.1)" }}>
          <Icon d={ICONS.layers} size={32} className="text-cyan-400" />
        </div>
        <div className="text-center">
          <p className="text-cyan-400 font-bold text-sm tracking-widest uppercase mb-1">ENGINEERED</p>
          <p className="text-slate-500 text-xs leading-relaxed">Structured intelligence.<br />Ready for decisions.</p>
        </div>
      </div>
    </div>

    {/* Mobile */}
    <div className="md:hidden flex flex-col items-center gap-3 max-w-sm mx-auto">
      {[
        { label: "RAW", icon: ICONS.spark, color: "red", border: "border-red-800/30", bg: "bg-red-900/20", text: "text-red-400", desc: "Unformed signal. Potential without structure." },
        { label: "HEKA", icon: ICONS.heka, color: "violet", border: "border-violet-800/40", bg: "bg-violet-900/20", text: "text-violet-400", desc: "Intelligence engine. Pattern from noise." },
        { label: "ENGINEERED", icon: ICONS.layers, color: "cyan", border: "border-cyan-800/30", bg: "bg-cyan-900/20", text: "text-cyan-400", desc: "Structured intelligence. Ready for decisions." },
      ].map((item, i) => (
        <React.Fragment key={i}>
          <div className={`flex items-center gap-4 w-full rounded-xl ${item.border} bg-slate-900 px-5 py-4`}>
            <div className={`w-12 h-12 rounded-xl ${item.border} ${item.bg} flex items-center justify-center flex-shrink-0`}>
              <Icon d={item.icon} size={22} className={item.text} />
            </div>
            <div>
              <p className={`${item.text} font-bold text-sm tracking-widest uppercase mb-0.5`}>{item.label}</p>
              <p className="text-slate-500 text-xs leading-relaxed">{item.desc}</p>
            </div>
          </div>
          {i < 2 && (
            <svg width="20" height="28" viewBox="0 0 20 28" fill="none">
              <line x1="10" y1="0" x2="10" y2="18" stroke="rgba(167,139,250,0.4)" strokeWidth="1.5" className="hp-flow-line" />
              <polyline points="4,12 10,22 16,12" stroke="rgba(167,139,250,0.5)" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </React.Fragment>
      ))}
    </div>
  </section>
);

// ─── PLATFORMS SECTION ─────────────────────────────────────────────────────────
const PlatformsSection = ({ setCurrentPage }) => (
<section className="hp-fade hp-fade-3 py-16">
      <div className="text-center mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Where We Build</p>
      <h2 className="text-2xl font-semibold text-slate-100 tracking-tight mb-3">Two Platforms. One Vision.</h2>
      <p className="text-slate-400 max-w-xl mx-auto text-sm leading-relaxed">
        HEKAOS operates through two distinct but interconnected platforms — one for building products, one for building intelligence.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">

      {/* FOUNDRY */}
      <div
        onClick={() => setCurrentPage("Products")}
        className="hp-card hp-platform-foundry rounded-2xl border border-amber-800/30 bg-slate-900 p-8 cursor-pointer overflow-hidden relative group">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 30% 70%, rgba(251,191,36,0.07) 0%, transparent 60%)" }} />

        <div className="w-14 h-14 rounded-xl bg-amber-900/30 border border-amber-800/40 flex items-center justify-center mb-5">
          <Icon d={ICONS.foundry} size={26} className="text-amber-400" />
        </div>

        <p className="text-xs font-semibold tracking-widest uppercase text-amber-500/60 mb-1">HEKAOS</p>
        <h3 className="text-amber-400 font-bold text-2xl tracking-tight mb-2">Foundry</h3>
        <p className="text-amber-300/60 text-xs font-semibold tracking-wide mb-4">Every product begins as an idea about how intelligence should work.</p>
        <p className="text-slate-400 text-sm leading-relaxed mb-6">
          Where ideas are tested under heat, shaped under pressure, and hardened into products that operate in the real world.
        </p>

        {/* Mini product tags */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["SpecStudio", "CareerSpec", "HireLoop"].map((p) => (
            <span key={p} className="text-xs px-3 py-1 rounded-full text-amber-300/70"
              style={{ background: "rgba(251,191,36,0.07)", border: "1px solid rgba(251,191,36,0.2)" }}>
              {p}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-2 text-amber-400 text-sm font-semibold group-hover:gap-3 transition-all">
          <span>Explore Foundry</span>
          <Icon d={ICONS.arrow} size={16} />
        </div>
      </div>

      {/* NOESIS */}
      <div
        onClick={() => setCurrentPage("Intelligence Corner")}
        className="hp-card hp-platform-noesis rounded-2xl border border-violet-800/30 bg-slate-900 p-8 cursor-pointer overflow-hidden relative group">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 70% 30%, rgba(139,92,246,0.07) 0%, transparent 60%)" }} />

        <div className="w-14 h-14 rounded-xl bg-violet-900/30 border border-violet-800/40 flex items-center justify-center mb-5">
          <Icon d={ICONS.noesis} size={26} className="text-violet-400" />
        </div>

        <p className="text-xs font-semibold tracking-widest uppercase text-violet-500/60 mb-1">HEKAOS</p>
        <h3 className="text-violet-400 font-bold text-2xl tracking-tight mb-2">NOESIS</h3>
        <p className="text-violet-300/60 text-xs font-semibold tracking-wide mb-4">Intelligence infrastructure for platforms that reason.</p>
        <p className="text-slate-400 text-sm leading-relaxed mb-6">
          The intelligence layer — SDKs, studio components, live intel feeds, and enterprise core infrastructure.
        </p>

        {/* Mini platform tags */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["NOESIS SDK", "NOESIS Studio", "NOESIS Intel", "NOESIS Core"].map((p) => (
            <span key={p} className="text-xs px-3 py-1 rounded-full text-violet-300/70"
              style={{ background: "rgba(139,92,246,0.07)", border: "1px solid rgba(139,92,246,0.2)" }}>
              {p}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-2 text-violet-400 text-sm font-semibold group-hover:gap-3 transition-all">
          <span>Explore NOESIS</span>
          <Icon d={ICONS.arrow} size={16} />
        </div>
      </div>
    </div>
  </section>
);


// ─── PRINCIPLES BENTO ──────────────────────────────────────────────────────────

// Adaptive Intelligence accent - Neural network nodes
const NeuralAccent = () => (
  <div className="relative h-20 flex items-center justify-center">
    <svg width="120" height="80" viewBox="0 0 120 80">
      <g className="vp-neural-connections">
        <line x1="30" y1="20" x2="60" y2="40" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />
        <line x1="30" y1="60" x2="60" y2="40" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />
        <line x1="60" y1="40" x2="90" y2="20" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />
        <line x1="60" y1="40" x2="90" y2="60" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />
      </g>
      <circle cx="30" cy="20" r="4" fill="rgba(6,182,212,0.4)" className="vp-neural-node" style={{ animationDelay: '0s' }} />
      <circle cx="30" cy="60" r="4" fill="rgba(6,182,212,0.4)" className="vp-neural-node" style={{ animationDelay: '0.2s' }} />
      <circle cx="60" cy="40" r="5" fill="rgba(6,182,212,0.6)" className="vp-neural-node" style={{ animationDelay: '0.4s' }} />
      <circle cx="90" cy="20" r="4" fill="rgba(6,182,212,0.4)" className="vp-neural-node" style={{ animationDelay: '0.6s' }} />
      <circle cx="90" cy="60" r="4" fill="rgba(6,182,212,0.4)" className="vp-neural-node" style={{ animationDelay: '0.8s' }} />
    </svg>
  </div>
);

// Objective First - Goal hierarchy: goal node at top, architecture flows below
const ObjectiveAccent = () => (
  <div className="relative h-20 flex items-center justify-center overflow-hidden">
    <svg width="120" height="80" viewBox="0 0 120 80" fill="none">
      {/* Lines from goal down to architecture nodes */}
      <line x1="60" y1="18" x2="30" y2="55" stroke="rgba(245,158,11,0.25)" strokeWidth="1.5" strokeDasharray="3 3" />
      <line x1="60" y1="18" x2="60" y2="55" stroke="rgba(245,158,11,0.25)" strokeWidth="1.5" strokeDasharray="3 3" />
      <line x1="60" y1="18" x2="90" y2="55" stroke="rgba(245,158,11,0.25)" strokeWidth="1.5" strokeDasharray="3 3" />
      {/* Goal node — top, brighter */}
      <circle cx="60" cy="14" r="6" fill="rgba(245,158,11,0.7)" className="vp-neural-node" style={{ animationDelay: '0s' }} />
      <circle cx="60" cy="14" r="10" fill="none" stroke="rgba(245,158,11,0.2)" strokeWidth="1" />
      {/* Architecture nodes — bottom row, dimmer */}
      <rect x="22" y="50" width="16" height="10" rx="3" fill="rgba(245,158,11,0.2)" stroke="rgba(245,158,11,0.3)" strokeWidth="1" />
      <rect x="52" y="50" width="16" height="10" rx="3" fill="rgba(245,158,11,0.2)" stroke="rgba(245,158,11,0.3)" strokeWidth="1" />
      <rect x="82" y="50" width="16" height="10" rx="3" fill="rgba(245,158,11,0.2)" stroke="rgba(245,158,11,0.3)" strokeWidth="1" />
      {/* Label */}
      <text x="60" y="76" textAnchor="middle" fontSize="7" fill="rgba(245,158,11,0.35)" fontFamily="monospace" letterSpacing="2">GOAL · ARCH</text>
    </svg>
  </div>
);

// Output Over Process - Signal pulse: faint process line, bold output spike
const OutputAccent = () => (
  <div className="relative h-16 flex items-center justify-center overflow-hidden">
    <svg width="120" height="56" viewBox="0 0 120 56" fill="none">
      {/* Baseline process line — faint, flat */}
      <polyline
        points="10,36 30,34 50,37 70,35 90,36 110,35"
        stroke="rgba(236,72,153,0.2)" strokeWidth="1.5" strokeLinejoin="round" fill="none"
      />
      {/* Output spike — bright, sharp */}
      <polyline
        points="10,36 40,36 55,10 70,36 110,36"
        stroke="rgba(236,72,153,0.7)" strokeWidth="2" strokeLinejoin="round" fill="none"
        className="vp-scan-line"
      />
      {/* Output peak dot */}
      <circle cx="55" cy="10" r="3.5" fill="rgba(236,72,153,0.9)" className="vp-neural-node" style={{ animationDelay: '0s' }} />
      <circle cx="55" cy="10" r="7" fill="none" stroke="rgba(236,72,153,0.2)" strokeWidth="1" />
      {/* Labels */}
      <text x="18" y="50" fontSize="6.5" fill="rgba(236,72,153,0.25)" fontFamily="monospace" letterSpacing="1.5">PROCESS</text>
      <text x="72" y="18" fontSize="6.5" fill="rgba(236,72,153,0.5)" fontFamily="monospace" letterSpacing="1.5">OUTPUT</text>
    </svg>
  </div>
);
// Principle accents — same ones from philosophy page
const PatternAccent = () => (
  <svg width="80" height="52" viewBox="0 0 80 52" fill="none">
    <defs>
      <pattern id="hp-grid" x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
        <circle cx="8" cy="8" r="1" fill="rgba(2, 1, 1, 0.25)" />
      </pattern>
    </defs>
    <rect width="80" height="52" fill="url(#hp-grid)" />
    <line x1="0" y1="26" x2="80" y2="26" stroke="rgba(245,158,11,0.6)" strokeWidth="1.5" className="hp-flow-line" />
  </svg>
);

const TargetAccent = () => (
  <div className="relative h-12 flex items-center justify-center">
    {[0, 1, 2].map(i => (
      <div key={i} className="absolute rounded-full border"
        style={{
          width: 16 + i * 14, height: 16 + i * 14,
          borderColor: `rgba(236,72,153,${0.5 - i * 0.12})`,
          borderWidth: 1.5,
          animation: `hp-node-pulse 2s ease-in-out ${i * 0.25}s infinite`,
        }} />
    ))}
    <div className="absolute w-3 h-3 rounded-full bg-pink-400"
      style={{ boxShadow: "0 0 10px rgba(236,72,153,0.8)" }} />
  </div>
);

// Iterative Convergence - Spiral loops tightening toward a center point
const ConvergenceAccent = () => (
  <div className="relative h-16 flex items-center justify-center overflow-hidden">
    <svg width="120" height="56" viewBox="0 0 120 56" fill="none">
      {/* Outer loop — faint */}
      <ellipse cx="60" cy="28" rx="48" ry="20" stroke="rgba(139,92,246,0.15)" strokeWidth="1.5" strokeDasharray="4 3" />
      {/* Mid loop */}
      <ellipse cx="60" cy="28" rx="32" ry="13" stroke="rgba(139,92,246,0.3)" strokeWidth="1.5" strokeDasharray="4 3" />
      {/* Inner loop — brightest */}
      <ellipse cx="60" cy="28" rx="16" ry="7" stroke="rgba(139,92,246,0.55)" strokeWidth="1.5" strokeDasharray="4 3" />
      {/* Convergence point */}
      <circle cx="60" cy="28" r="3.5" fill="rgba(139,92,246,0.9)" className="vp-neural-node" style={{ animationDelay: '0s' }} />
      <circle cx="60" cy="28" r="6" fill="none" stroke="rgba(139,92,246,0.3)" strokeWidth="1" />
      {/* Iteration arrows hint */}
      <text x="60" y="52" textAnchor="middle" fontSize="8" fill="rgba(167,139,250,0.7)" fontFamily="monospace" letterSpacing="1">ITERATE · CONVERGE</text>
    </svg>
  </div>
);

// Adaptive Calibration — Gauge dial with needle locking onto target zone
const CalibrationAccent = () => (
  <div className="relative h-16 flex items-center justify-center overflow-hidden">
    <svg width="100" height="56" viewBox="0 0 100 56" fill="none">
      
      {/* Gauge arc — full range */}
      <path d="M12,50 A38,38 0 0,1 88,50" stroke="rgba(6,182,212,0.15)" strokeWidth="4" strokeLinecap="round" fill="none" />
      
      {/* Danger zone — left, red-ish */}
      <path d="M12,50 A38,38 0 0,1 33,18" stroke="rgba(6,182,212,0.25)" strokeWidth="4" strokeLinecap="round" fill="none" />
      
      {/* Calibrated zone — right of center, bright cyan */}
      <path d="M50,12 A38,38 0 0,1 88,50" stroke="rgba(6,182,212,0.75)" strokeWidth="4" strokeLinecap="round" fill="none" />

      {/* Tick marks */}
      {[0, 30, 60, 90, 120, 150, 180].map((angle, i) => {
        const rad = ((angle - 180) * Math.PI) / 180;
        const x1 = 50 + 34 * Math.cos(rad);
        const y1 = 50 + 34 * Math.sin(rad);
        const x2 = 50 + 28 * Math.cos(rad);
        const y2 = 50 + 28 * Math.sin(rad);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={`rgba(6,182,212,${i > 3 ? 0.6 : 0.2})`} strokeWidth="1.5" strokeLinecap="round" />;
      })}

      {/* Needle — pointing to calibrated zone */}
      <line x1="50" y1="50" x2="74" y2="20"
        stroke="rgba(6,182,212,0.9)" strokeWidth="2" strokeLinecap="round" />

      {/* Needle pivot */}
      <circle cx="50" cy="50" r="4" fill="rgba(6,182,212,1)" />
      <circle cx="50" cy="50" r="7" fill="none" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />

      {/* Lock dot on arc — where needle points */}
      <circle cx="76" cy="18" r="3" fill="rgba(6,182,212,0.9)" className="vp-neural-node" style={{ animationDelay: '0s' }} />

    </svg>
  </div>
);

const PrinciplesSection = () => (
<section className="hp-fade hp-fade-5 py-16">
      <div className="text-center mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">How We Think</p>
      <h2 className="text-2xl font-semibold text-slate-100 tracking-tight mb-3">Outcome-Based Strategy</h2>
      <p className="text-slate-400 max-w-xl mx-auto text-sm leading-relaxed">
        Every system has an objective function. Ours is real-world impact — defined, tracked, and refined until the result is undeniable.
      </p>
    </div>

    {/* Bento grid: 1 large + 3 smaller */}
    <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-4 md:h-[480px]">

      {/* Objective First - Large card spanning 2 rows */}
      <div className="vp-principle-card md:row-span-2 rounded-2xl border border-amber-800/30 
        hover:border-amber-500/40 bg-slate-900 p-7 flex flex-col justify-between overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 20% 80%, rgba(251,191,36,0.06) 0%, transparent 60%)" }} />

        <div>
          <div className="w-12 h-12 rounded-xl bg-amber-900/30 border border-amber-800/40 flex items-center justify-center mb-5">
            <Icon d={ICONS.eye} size={22} className="text-amber-400" />
          </div>
          <h3 className="text-amber-400 font-bold text-lg tracking-tight mb-2">Objective First </h3>
          <p className="text-slate-400 text-sm leading-relaxed">
            The architecture follows the goal — never the other way around. We define success before we design the system that achieves it. Most systems fail not because of poor execution, but because the objective was never precisely defined. At HEKAOS, clarity of intent is the first engineering problem we solve — everything else is downstream of that.
          </p>
        </div>
        <div className="mt-6 relative h-20 flex items-end">
          <ObjectiveAccent />
          <p className="absolute bottom-0 right-0 text-xs text-amber-400/40  tracking-wide uppercase">
            Intent · Blueprint · Execute
          </p>
        </div>
      </div>

      {/* Output Over Process */}
      {/* Output Over Process */}
      <div className="vp-principle-card rounded-2xl border border-pink-800/30 hover:border-pink-500/40 
  bg-slate-900 p-6 flex flex-col justify-between overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 80% 20%, rgba(236,72,153,0.06) 0%, transparent 60%)" }} />
        <div>
          {/* Icon + Accent in same row */}
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-pink-900/30 border border-pink-800/40 flex items-center justify-center">
              <Icon d={ICONS.target} size={20} className="text-pink-400" />
            </div>
            <OutputAccent />
          </div>

          <h3 className="text-pink-400 font-bold text-base tracking-tight mb-1.5">Output Over Process</h3>
          <p className="text-slate-400 text-xs leading-relaxed">
            What a system produces is the only metric that matters. We measure real-world results, not internal activity.
          </p>
        </div>
      </div>

      {/* Iterative Convergence */}
      <div className="vp-principle-card rounded-2xl border border-violet-800/30 hover:border-violet-500/40 
  bg-slate-900 p-6 flex flex-col justify-between overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(139,92,246,0.06) 0%, transparent 60%)" }} />
        <div>
          {/* Icon + Accent in same row */}
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-violet-900/30 border border-violet-800/40 flex items-center justify-center">
              <Icon d={ICONS.compass} size={20} className="text-violet-400" />
            </div>
            <ConvergenceAccent />
          </div>

          <h3 className="text-violet-400 font-bold text-base tracking-tight mb-1.5">Iterative Convergence</h3>
          <p className="text-slate-400 text-xs leading-relaxed">
            We build, test, and close the gap between intended and actual outcomes — repeatedly, until the result holds
          </p>
        </div>
      </div>

      {/* Adaptive Calibration */}
      <div className="vp-principle-card md:col-span-2 rounded-2xl border border-cyan-800/25 hover:border-cyan-500/35 
        bg-slate-900 p-6 flex items-center gap-8 overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 100% 50%, rgba(6,182,212,0.05) 0%, transparent 60%)" }} />
        <NeuralAccent />
        <div className="flex-1 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-cyan-900/25 border border-cyan-800/35 flex items-center justify-center mb-4">
            <Icon d={ICONS.spark} size={20} className="text-cyan-400" />
          </div>
          <h3 className="text-cyan-400 font-bold text-base tracking-tight mb-1.5">Adaptive Calibration</h3>
          <p className="text-slate-400 text-xs leading-relaxed">
            When the environment shifts, the objective function evolves with it. Static strategy is a liability in dynamic systems.
          </p>
        </div>
      </div>

    </div>
  </section>
);



// ─── HOMEPAGE ──────────────────────────────────────────────────────────────────
const HomePage = ({ setCurrentPage = () => { } }) => (
  <>
    <style>{styles}</style>
    <div className="flex flex-col">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <OrbitingTagCloud page="home" />
        <div className="relative z-10 text-center max-w-4xl">
          <h1 className="hekaos-title text-4xl md:text-4xl font-bold mb-2 tracking-wide">
            /ˈhē-kā-ˌōs/
          </h1>
          <p className="max-w-xl mx-auto text-sm text-stone-300 leading-relaxed space-y-4">
            <span className="block hekaos-line">
              HEKAOS is a system for engineering <span className="text-amber-400">order</span> from{' '}
              <span className="text-violet-400">chaos</span>—where data, models, and human intent converge into{' '}
              <span className="text-amber-400">clarity</span> and <span className="text-amber-400">decisive action</span>.
            </span>
            <span className="block hekaos-line">
              A modern <span className="text-violet-400">philosophy of intelligence design</span>: we blend ancient pattern-sense
              with <span className="text-amber-400">machine reasoning</span> to simulate futures, expose tradeoffs, and shape outcomes early.
            </span>
            <span className="block hekaos-line">
              An ecosystem of <span className="text-stone-200">minds</span> and <span className="text-amber-400">machines</span> that turns{' '}
              <span className="text-violet-400">uncertainty</span> into <span className="text-amber-400">structured intelligence</span>—and intelligence into durable value.
            </span>
            <span className="block hekaos-line font-medium text-stone-200">
              In practice: <span className="font-semibold text-stone-100">HEKAOS</span> is not a single tool—it is an{' '}
              <span className="text-amber-400">architecture</span> for making intelligence <span className="text-violet-400">intentional</span>.
            </span>
          </p>
        </div>
      </section>

      <div className="flex flex-col max-w-6xl mx-auto px-4 pb-20">
        <div className="hp-divider" />
        <TransformationCycle />   {/* py-16 inside */}
        <div className="hp-divider" />
        <PrinciplesSection />     {/* py-16 inside */}
        <div className="hp-divider" />
        <PlatformsSection setCurrentPage={setCurrentPage} />     {/* py-16 inside */}

        <div className="hp-divider" />

        <PageCTA page="Home" setCurrentPage={setCurrentPage} />

      </div>
    </div>
  </>
);

export default HomePage;